import { ResetPasswordForm } from "@/routes/auth/_auth-layout/-components/auth/reset-password.form";
import { FORGOT_PASSWORD_ERROR } from "@/lib/utils";
import { Navigate, createFileRoute } from "@tanstack/react-router";
import { z } from "zod/v4";
import { AuthCard } from "@/routes/auth/_auth-layout/-components/auth/auth-card";

export const Route = createFileRoute(
  "/auth/_auth-layout/reset-password/$token",
)({
  component: () => <ResetPassword />,
});

function ResetPassword() {
  const params = Route.useParams();

  const parsed = parseToken(params.token);

  if (!parsed || isExpired(parsed)) {
    return (
      <Navigate
        to="/auth/forgot-password"
        search={{ error: FORGOT_PASSWORD_ERROR.EXPIRED }}
      />
    );
  }

  return (
    <AuthCard
      backTo="/auth/signin"
      title="Reset your password"
      description="Choose a strong password with at least 8 characters."
      footer="Your new password will be required on your next sign-in."
    >
      <ResetPasswordForm token={parsed.token} />
    </AuthCard>
  );
}

const tokenSchema = z.object({
  token: z.string(),
  type: z.string(),
  isUsed: z.boolean(),
  expiresAt: z.string(),
});
type Token = z.infer<typeof tokenSchema>;

export function parseToken(token: string): Token | null {
  try {
    const decoded = atob(token);
    const parsed = JSON.parse(decoded) as unknown;
    const validated = tokenSchema.safeParse(parsed);

    if (!validated.success) {
      return null;
    }

    return validated.data;
  } catch {
    return null;
  }
}

function isExpired(parsedToken: Token) {
  if (parsedToken.isUsed) {
    return true;
  }
  const date = new Date(parsedToken.expiresAt);
  const now = new Date();
  if (now > date) {
    return true;
  }
  return false;
}
